/// <reference types="@emotion/react/types/css-prop" />
import {
  ContentModule,
  ModuleProperties,
  useAnimationStates,
  useShowInstructions,
} from '@backstage-components/base';
import {css} from '@emotion/react';
import {FC} from 'react';
import * as Styled from './Date.styled';
import {ComponentDefinition, SchemaType} from './DateDefinition';

export interface IDateProps extends ModuleProperties, SchemaType {}

export type DateComponentDefinition = ContentModule<
  'Date',
  IDateProps & SchemaType
>;

export const DateComponent: FC<DateComponentDefinition> = (definition) => {
  const styles = css`
    ${definition.style}
    ${definition.props.styleAttr}
  `;
  const {props} = definition;
  const {timestamp, showTimeZone, backgroundColor, textColor, animationStates} =
    props;
  const {observable, broadcast} = useShowInstructions(
    ComponentDefinition.instructions,
    definition
  );
  const motionOptions = useAnimationStates(
    observable,
    broadcast,
    animationStates
  );

  // Note: this implementation naively assumes the user's browser supports the Intl API,
  // which may not be true across some older or uncommon browsers.
  const userLocale = navigator.language;
  const date = new Date(timestamp);
  const month = date.toLocaleString(userLocale, {month: 'long'});
  const day = date.toLocaleDateString(userLocale, {day: '2-digit'});
  const weekday = date.toLocaleString(userLocale, {weekday: 'long'});
  const time = date.toLocaleTimeString(userLocale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: showTimeZone ? 'short' : undefined,
  });

  return (
    <Styled.Container
      className="date__container"
      data-testid="container"
      backgroundColor={backgroundColor}
      css={styles}
      {...motionOptions}
    >
      <Styled.Section className="date__section">
        <Styled.Header
          className="date__header"
          data-testid="month"
          textColor={textColor}
        >
          {month}
        </Styled.Header>
        <Styled.Value
          className="date__value"
          data-testid="day"
          textColor={textColor}
        >
          {day}
        </Styled.Value>
      </Styled.Section>
      <Styled.Divider
        className="date__divider"
        data-testid="divider"
        textColor={textColor}
      />
      <Styled.Section className="date__section">
        <Styled.Header
          className="date__header"
          data-testid="weekday"
          textColor={textColor}
        >
          {weekday}
        </Styled.Header>
        <Styled.Value
          className="date__value"
          data-testid="time"
          textColor={textColor}
        >
          {time}
        </Styled.Value>
      </Styled.Section>
    </Styled.Container>
  );
};
